import { hideScrollBar, IconFont, singleTextEllipsis } from '@byecode/ui'
import type { ApplicationSettingThemeColor, TabsColorConfig, VariableADTvalue } from '@lighthouse/core'
import {
    applyFillPickerValue4CssColorValue,
    combineBackgroundStyle,
    getBackgroundStyle,
    getBorderStyle,
    getImageFullUrlInApplication,
    getRadiusStyle,
    getShadowStyle,
    getTextColorStyle,
    getVeinsStyle,
    IconRender,
    TEXT_FONT_NORMAL,
    useApplicationContext,
    useFillPickerContext,
    useMediaQueryRemBase,
    useUnifiedTap
} from '@lighthouse/shared'
import classnames from 'classnames'
import type { FC } from 'react'
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { throttle } from 'throttle-debounce'

import type { BaseTabsProps } from '../Tabs.type'
import { TabsContainer } from '../TabsContainer'
import { TabsController } from '../TabsController'

const TabsWrapper = styled.div`
    position: relative;
    overflow: hidden;
`

const TabsContent = styled.div`
    display: flex;
    gap: 8px;
    overflow-x: auto;
    /* ${hideScrollBar()} */
`

const TabLabel = styled.div`
    flex: 1;
`

const TabIcon = styled(IconRender)``

type TabProps = {
    appId: string | undefined
    lineClamp?: number
    normal?: TabsColorConfig
    hover?: TabsColorConfig
    active?: TabsColorConfig
    palettes: ApplicationSettingThemeColor[]
    parseVariableImage?: (value: VariableADTvalue) => string
    parseVariableColor?: (value: VariableADTvalue) => string
}

const Tab = styled.button.withConfig<TabProps>({
    shouldForwardProp: p =>
        !['appId', 'palettes', 'parseVariableImage', 'parseVariableColor', 'lineClamp', 'normal', 'hover', 'active'].includes(p)
})`
    all: unset;
    box-sizing: border-box;
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    border-radius: 4px;
    padding: 9px 8px;
    border: 1px solid transparent;
    white-space: nowrap;

    ${TabLabel} {
        ${({ lineClamp }) => {
            if (!lineClamp) {
                return
            }

            return lineClamp === 1
                ? singleTextEllipsis
                : css`
                      display: -webkit-box;
                      -webkit-box-orient: vertical;
                      -webkit-line-clamp: ${lineClamp};
                      overflow: hidden;
                  `
        }};
    }

    ${({ appId, normal, hover, active, parseVariableImage, parseVariableColor, palettes }) => {
        return {
            '&:not([data-active])&:not(:hover)': {
                ...getBackgroundStyle({ appId, value: normal?.background, palettes, parseVariableImage, parseVariableColor }),
                borderColor: applyFillPickerValue4CssColorValue(normal?.borderColor?.color, palettes),
                [TabLabel]: {
                    ...getTextColorStyle(normal?.color, palettes)
                },
                [TabIcon]: {
                    ...getTextColorStyle(normal?.iconColor, palettes)
                }
            },

            '&:not([data-active]):hover': {
                ...getBackgroundStyle({ appId, value: hover?.background, palettes, parseVariableImage, parseVariableColor }),
                borderColor: applyFillPickerValue4CssColorValue(hover?.borderColor?.color, palettes) || 'transparent',
                [TabLabel]: {
                    ...getTextColorStyle(hover?.color, palettes)
                },
                [TabIcon]: {
                    ...getTextColorStyle(hover?.iconColor, palettes)
                }
            },

            '&.active': {
                ...getBackgroundStyle({ appId, value: active?.background, palettes, parseVariableImage, parseVariableColor }),
                borderColor: applyFillPickerValue4CssColorValue(active?.borderColor?.color, palettes) || 'transparent',
                [TabLabel]: {
                    ...getTextColorStyle(active?.color, palettes)
                },
                [TabIcon]: {
                    ...getTextColorStyle(active?.iconColor, palettes)
                }
            }
        }
    }};
`

const Indicator = styled.span`
    position: absolute;
    left: 0;
    bottom: 0;
    height: 2px;
    transition: all 0.2s;
`

interface LineTabsProps extends BaseTabsProps {
    scale?: number
}

export const LineTabs: FC<LineTabsProps> = ({
    scale = 1,
    value,
    onChange,
    onClickTab,
    align,
    design,
    font,
    list,
    parseVariableImage,
    parseVariableColor,
    ...rest
}) => {
    const { background, veins, shadow, border, radius, lineColor } = design ?? {}
    const { fontFamily, fontStyle, fontWeight, fontSize, letterSpacing, lineClamp, lineHeight, align: textAlign } = font ?? {}
    const { appId, isBuilder } = useApplicationContext()
    const index = useMemo(() => list.findIndex(item => item.id === value), [list, value])

    const contentRef = useRef<null | HTMLDivElement>(null)

    // const [isOverflow, setIsOverflow] = useState(false)
    const [indicatorInfo, setIndicatorInfo] = useState({ width: 0, left: 0 })

    const handleTabTap = useCallback(
        (e: React.MouseEvent<HTMLElement> | React.TouchEvent<HTMLElement>, id: string) => {
            onClickTab?.(e as React.MouseEvent<HTMLButtonElement>, id)
            if (value === id) return
            e.currentTarget.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' })
            onChange?.(id)
        },
        [onClickTab, value, onChange]
    )
    const unifiedTap = useUnifiedTap<[string]>(handleTabTap)

    useLayoutEffect(() => {
        const contentEl = contentRef.current
        if (!contentEl) {
            return
        }

        // 检测是否出现滚动条、以及检测选中的tab位置和尺寸
        const handler: ResizeObserverCallback = ([e]) => {
            // setIsOverflow(Math.round(e.target.scrollWidth) > Math.round(e.contentRect.width))
            const activeEl = e.target.children.item(index)
            if (!activeEl) {
                return
            }
            const contentRect = contentEl.getBoundingClientRect()
            const rect = activeEl.getBoundingClientRect()
            const pl = Number.parseFloat(window.getComputedStyle(activeEl).paddingLeft)
            const pr = Number.parseFloat(window.getComputedStyle(activeEl).paddingRight)
            setIndicatorInfo({ width: (rect.width - pl - pr) / scale, left: (pl + rect.x - contentRect.x) / scale })
        }
        const observer = new ResizeObserver(handler)

        observer.observe(contentEl)

        // 滚动时更改指示器的定位
        const scrollHandler = throttle(100, (e: Event) => {
            if (e.target instanceof Element) {
                const activeEl = e.target.children.item(index)?.querySelector('*')
                if (!activeEl) {
                    return
                }
                const contentRect = contentEl.getBoundingClientRect()
                const rect = activeEl.getBoundingClientRect()
                const pl = Number.parseFloat(window.getComputedStyle(activeEl).paddingLeft)

                setIndicatorInfo(s => ({ ...s, left: (pl + rect.x - contentRect.x) / scale }))
            }
        })
        contentEl.addEventListener('scroll', scrollHandler)

        return () => {
            observer.unobserve(contentEl)
            contentEl.removeEventListener('scroll', scrollHandler)
        }
    }, [index, scale /* allSizeFitDep */])

    const { palettes } = useFillPickerContext()
    const rootFontSize = useMediaQueryRemBase()

    const fs = useMemo(() => {
        if (!fontSize || !fontSize.size) {
            return
        }

        let rawFontSize
        if (fontSize?.unit === 'rem' && isBuilder) {
            rawFontSize = `${rootFontSize * (fontSize.size || 0)}px`
        } else {
            rawFontSize = `${fontSize.size}${fontSize?.unit || 'px'}`
        }

        return rawFontSize
    }, [fontSize, isBuilder, rootFontSize])

    return (
        <TabsContainer style={{ justifyContent: align }} {...rest}>
            {/* {isOverflow && (
                <TabsController
                    onClick={() => {
                        const el = contentRef.current
                        if (!el) {
                            return
                        }
                        const to = Math.max(0, el.scrollLeft - el.clientWidth)
                        el.scrollTo({
                            left: to,
                            behavior: 'smooth'
                        })
                    }}
                >
                    <IconFont type="ArrowLeftSmall" />
                </TabsController>
            )} */}

            <TabsWrapper
                style={{
                    // flex: hasTabSizeFill ? 1 : undefined,
                    ...combineBackgroundStyle([
                        getBackgroundStyle({ appId, value: background, palettes, parseVariableImage, parseVariableColor }),
                        getVeinsStyle(veins, palettes)
                    ]),
                    ...getShadowStyle(shadow, palettes),
                    ...getBorderStyle(border, palettes),
                    ...getRadiusStyle(radius)
                }}
            >
                <TabsContent ref={contentRef}>
                    {list.map(tab => (
                        <Tab
                            key={tab.id}
                            type="button"
                            data-active={tab.id === value || undefined}
                            className={classnames({ active: tab.id === value })}
                            onClick={e => unifiedTap.onClick(e, tab.id)}
                            onTouchStart={e => unifiedTap.onTouchStart(e, tab.id)}
                            onTouchMove={e => unifiedTap.onTouchMove(e, tab.id)}
                            onTouchEnd={e => unifiedTap.onTouchEnd(e, tab.id)}
                            style={{
                                // flex: tab.sizeFit === SIZE_FIT.fill ? 1 : undefined,
                                fontFamily: fontFamily === TEXT_FONT_NORMAL ? undefined : fontFamily,
                                fontStyle,
                                fontWeight,
                                fontSize: fs,
                                letterSpacing: `${letterSpacing ?? 0}rem`,
                                lineHeight: `${lineHeight ?? 1}`,
                                textAlign
                            }}
                            appId={appId}
                            lineClamp={lineClamp}
                            parseVariableImage={parseVariableImage}
                            parseVariableColor={parseVariableColor}
                            palettes={palettes}
                            normal={{
                                background: tab.background,
                                color: tab.color,
                                iconColor: tab.iconColor,
                                borderColor: tab.borderColor
                            }}
                            hover={tab.hover}
                            active={tab.active}
                        >
                            {tab.tabIcon?.value && (
                                <TabIcon
                                    size={16}
                                    isCompact
                                    readonly
                                    styles={{
                                        root: tab.iconPosition === 'right' ? { order: 1 } : undefined
                                    }}
                                    value={tab.tabIcon}
                                    onGetFullUrl={url => getImageFullUrlInApplication(appId, url)}
                                />
                            )}
                            {/* {tab.icon && <TabIcon type={tab.icon} style={tab.iconPosition === 'right' ? { order: 1 } : undefined} />} */}
                            <TabLabel>{tab.name}</TabLabel>
                        </Tab>
                    ))}
                </TabsContent>
                <Indicator
                    style={{
                        transform: `translateX(${indicatorInfo.left}px)`,
                        // left: indicatorInfo.left,
                        width: indicatorInfo.width,
                        ...getBackgroundStyle({ appId, value: lineColor, palettes, parseVariableColor })
                    }}
                />
            </TabsWrapper>

            {/* {isOverflow && (
                <TabsController
                    onClick={() => {
                        const el = contentRef.current
                        if (!el) {
                            return
                        }
                        const to = Math.min(el.scrollWidth, el.scrollLeft + el.clientWidth)
                        el.scrollTo({
                            left: to,
                            behavior: 'smooth'
                        })
                    }}
                >
                    <IconFont type="ArrowRightSmall" />
                </TabsController>
            )} */}
        </TabsContainer>
    )
}
